import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showFundraiseDropdown, setShowFundraiseDropdown] = useState(false);
  const [showDonateDropdown, setShowDonateDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  const [user, setUser] = useState(null);
  const [profileSheetOpen, setProfileSheetOpen] = useState(false);

  // Determine active tab based on current route
  useEffect(() => {
    const path = location.pathname;

    if (path === "/" || path.startsWith("/campaign")) {
      setActiveTab("home");
    } else if (path === "/about") {
      setActiveTab("about");
    } else if (
      path === "/donate" ||
      path.includes("/campaigns") ||
      path.includes("/categories")
    ) {
      setActiveTab("donate");
    } else if (path === "/start-fundraiser") {
      setActiveTab("fundraise");
    } else if (path === "/signin") {
      setActiveTab("signin");
    } else {
      setActiveTab("");
    }
  }, [location]);

  // Close all dropdowns when route changes
  useEffect(() => {
    setShowFundraiseDropdown(false);
    setShowDonateDropdown(false);
  }, [location]);

  // Detect logged in user from sessionStorage
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      setUser(null);
    }
  }, [location]);

  const navItems = [
    {
      name: "Fundraise",
      icon: "fa-chevron-down",
      path: "/start-fundraiser",
      hasDropdown: true,
      dropdownOpen: showFundraiseDropdown,
      setDropdownOpen: setShowFundraiseDropdown,
      tabKey: "fundraise",
    },
    {
      name: "About Us",
      path: "/about",
      tabKey: "about",
    },
    {
      name: "Contact Us",
      path: "/contact",
      tabKey: "contact",
    },
  ];

  // All purpose options
  const purposeOptions = [
    { value: "medical", label: "Medical Expenses", icon: "fa-hospital" },
    { value: "education", label: "Education Fees", icon: "fa-graduation-cap" },
    { value: "emergency", label: "Emergency Relief", icon: "fa-first-aid" },
    { value: "business", label: "Business Funding", icon: "fa-briefcase" },
    { value: "personal", label: "Personal Needs", icon: "fa-user" },
    { value: "creative", label: "Creative Projects", icon: "fa-paint-brush" },
    { value: "community", label: "Community Support", icon: "fa-users" },
    { value: "animals", label: "Animal Welfare", icon: "fa-paw" },
  ];

  const handlePurposeSelect = (purpose) => {
    localStorage.setItem("preselectedPurpose", purpose.value);
    window.location.href = "/start-fundraiser";
    setShowFundraiseDropdown(false);
  };

  const handleGeneralFundraiser = () => {
    localStorage.removeItem("preselectedPurpose");
    window.location.href = "/start-fundraiser";
    setShowFundraiseDropdown(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    setUser(null);
    setProfileSheetOpen(false);
    navigate("/signin");
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setShowFundraiseDropdown(false);
    setShowDonateDropdown(false);
  };

  // Handle mouse leave from navbar area
  const handleNavbarMouseLeave = () => {
    closeAllDropdowns();
    setHoveredItem(null);
  };

  // Check if tab is active
  const isTabActive = (tabKey) => {
    return activeTab === tabKey;
  };

  return (
    <nav
      className="flex sticky top-0 z-50 items-center justify-between px-4 md:px-8 py-3 bg-white shadow-md w-full min-h-[70px] animate-slideDown"
      onMouseLeave={handleNavbarMouseLeave}
    >
      {/* Left Section: Logo + Donate/Fundraise */}
      <div className="flex items-center gap-6 md:gap-8">
        {/* Logo */}
        <Link to="/" onClick={closeAllDropdowns}>
          <img
            src="/assets/image.jpg"
            alt="Logo"
            className="h-10 md:h-16 w-auto"
          />
        </Link>

        {/* Donate & Fundraise Links - Desktop */}
        <div className="hidden md:flex items-center gap-4">
          {navItems.map((item) => (
            <div key={item.name} className="relative">
              {item.hasDropdown ? (
                <div className="relative">
                  <button
                    className={`flex items-center px-4 py-2 rounded-full transition-all duration-300 ${
                      isTabActive(item.tabKey)
                        ? "bg-gradient-to-r from-teal-50 to-teal-100 text-teal-700 font-semibold border border-teal-200"
                        : "hover:bg-gray-50"
                    } ${item.name === "Fundraise" ? "group" : ""}`}
                    onMouseEnter={() => {
                      setHoveredItem(item.name);
                      setShowFundraiseDropdown(true);
                      setShowDonateDropdown(false);
                    }}
                    onMouseLeave={() => {
                      // Don't close immediately - let the dropdown handle it
                    }}
                    onClick={() => {
                      setShowFundraiseDropdown(!showFundraiseDropdown);
                      setShowDonateDropdown(false);
                    }}
                  >
                    {item.name}
                    <i
                      className={`fas ${
                        item.icon
                      } ml-2 text-xs transition-transform duration-300 ${
                        item.dropdownOpen || hoveredItem === item.name
                          ? "rotate-180"
                          : ""
                      }`}
                    ></i>

                    {item.name === "Fundraise" && (
                      <>
                        <span
                          className={`absolute bottom-0 left-4 right-4 h-0.5 ${
                            isTabActive(item.tabKey)
                              ? "bg-teal-600"
                              : "bg-teal-600 transform scale-x-0 group-hover:scale-x-100"
                          } transition-transform duration-300 origin-left`}
                        ></span>
                      </>
                    )}
                  </button>

                  {/* Fundraise Dropdown - Show only 3 options visible */}
                  {item.name === "Fundraise" && showFundraiseDropdown && (
                    <div
                      className="absolute left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-50"
                      onMouseEnter={() => setShowFundraiseDropdown(true)}
                      onMouseLeave={() => setShowFundraiseDropdown(false)}
                    >
                      <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-700 text-left text-sm">
                          Start Fundraising For
                        </h4>
                      </div>
                      
                      {/* Show only 3 options visible, rest scrollable */}
                      <div className="overflow-y-auto" style={{ maxHeight: "180px" }}>
                        {purposeOptions.map((option) => (
                          <div
                            key={option.value}
                            className="px-4 py-3 cursor-pointer transition-all hover:bg-teal-50 border-b border-gray-100 last:border-b-0"
                            onClick={() => handlePurposeSelect(option)}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor = "#f0fdfa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor = "")
                            }
                          >
                            <div className="flex items-center gap-3">
                              <i className={`fas ${option.icon} text-teal-600 text-sm`}></i>
                              <div className="font-medium text-gray-800 text-sm">
                                {option.label}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className="border-t border-gray-200 p-3 bg-gray-50">
                        <button
                          onClick={handleGeneralFundraiser}
                          className="block w-full text-center bg-gradient-to-r from-teal-600 to-teal-700 text-white text-sm font-semibold py-2 px-4 rounded-lg hover:from-teal-700 hover:to-teal-800 transition-all"
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.transform = "scale(1.02)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                        >
                          Start General Fundraiser
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-2 rounded-full transition-all duration-300 relative group ${
                    isTabActive(item.tabKey)
                      ? "text-teal-700 font-semibold bg-gradient-to-r from-teal-50 to-teal-100 border border-teal-200"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  onMouseEnter={() => setHoveredItem(item.name)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={closeAllDropdowns}
                >
                  {item.name}

                  {/* Active indicator for About Us */}
                  {isTabActive(item.tabKey) ? (
                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-teal-600"></span>
                  ) : (
                    <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-teal-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                  )}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Right Section: Sign in, Sign up, Start Fundraiser */}
      <div className="flex items-center gap-3 md:gap-4">
        {/* Sign in and Sign up buttons side by side - Desktop */}
        <div className="hidden md:flex items-center gap-2">
          {!user ? (
            <>
              {/* Sign in button */}
              <Link
                to="/signin"
                className={`px-4 py-2 rounded-full transition-all duration-300 hover:scale-[1.02] border min-w-[90px] text-center ${
                  isTabActive("signin")
                    ? "bg-teal-50 text-teal-700 font-semibold border-teal-200"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                onClick={closeAllDropdowns}
              >
                Sign in
              </Link>

              {/* Sign up button */}
              <Link
                to="/start-fundraiser"
                className={`px-4 py-2 rounded-full font-medium transition-all duration-300 hover:scale-[1.02] min-w-[100px] text-center ${
                  isTabActive("fundraise")
                    ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800"
                    : "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700"
                }`}
                onClick={closeAllDropdowns}
              >
                Sign up
              </Link>
            </>
          ) : (
            <>
              {/* Profile Icon */}
              <button
                onClick={() => setProfileSheetOpen(true)}
                className="w-10 h-10 rounded-full flex items-center justify-center bg-teal-50 border border-teal-200 text-teal-700 hover:bg-teal-100 transition-all"
              >
                <i className="fas fa-user"></i>
              </button>
            </>
          )}
        </div>

        {/* Start Fundraiser Button - Desktop */}
        {!user && (
          <Link
            to="/start-fundraiser"
            className={`hidden md:block relative px-5 py-2 rounded-full text-white font-medium transition-all duration-300 hover:scale-[1.05] group overflow-hidden shadow-lg hover:shadow-xl ml-2 ${
              isTabActive("fundraise")
                ? "bg-gradient-to-r from-teal-700 to-teal-800"
                : "bg-gradient-to-r from-[#00b7b3] to-[#009e9a]"
            }`}
            onClick={() => {
              closeAllDropdowns();
              handleGeneralFundraiser();
            }}
          >
            <span className="relative z-10">Start a Fundraiser</span>
            <span
              className={`absolute inset-0 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300 ${
                isTabActive("fundraise")
                  ? "bg-gradient-to-r from-teal-800 to-teal-900"
                  : "bg-gradient-to-r from-[#00d9d4] to-[#00b7b3]"
              }`}
            ></span>
          </Link>
        )}

        {/* Hamburger Menu for Mobile */}
        <button
          className="relative block md:hidden text-xl cursor-pointer p-2 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all duration-300"
          onClick={() => {
            setMenuOpen(!menuOpen);
            closeAllDropdowns();
          }}
          aria-label="Toggle menu"
        >
          <i
            className={`fas ${
              menuOpen ? "fa-times animate-spinIn" : "fa-bars"
            } transition-all duration-300`}
          ></i>
          {!menuOpen && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-teal-500 rounded-full animate-pulse"></span>
          )}
        </button>
      </div>

      {/* Mobile Menu with animation */}
      <div
        className={`absolute top-full left-0 right-0 md:hidden bg-white flex flex-col shadow-lg border-t overflow-hidden transition-all duration-500 ease-in-out ${
          menuOpen
            ? "max-h-[500px] opacity-100 translate-y-0"
            : "max-h-0 opacity-0 -translate-y-4 pointer-events-none"
        }`}
      >
        <div className="p-5 space-y-2">
          {/* About Us Link */}
          <Link
            to="/about"
            onClick={() => {
              setMenuOpen(false);
              closeAllDropdowns();
            }}
            className={`flex items-center px-4 py-3 rounded-lg transition-all duration-300 animate-slideIn ${
              isTabActive("about")
                ? "bg-gradient-to-r from-teal-50 to-white border-l-4 border-teal-600 font-semibold text-teal-700"
                : "hover:bg-gray-50"
            }`}
            style={{ animationDelay: "100ms" }}
          >
            <span>About Us</span>
          </Link>

          {/* Contact Us Link */}
          <Link
            to="/contact"
            onClick={() => {
              setMenuOpen(false);
              closeAllDropdowns();
            }}
            className={`flex items-center px-4 py-3 rounded-lg transition-all duration-300 animate-slideIn ${
              isTabActive("contact")
                ? "bg-gradient-to-r from-teal-50 to-white border-l-4 border-teal-600 font-semibold text-teal-700"
                : "hover:bg-gray-50"
            }`}
            style={{ animationDelay: "150ms" }}
          >
            <span>Contact Us</span>
          </Link>

          {/* Fundraise Dropdown for Mobile - Show only 3 options visible */}
          <div className="animate-slideIn" style={{ animationDelay: "200ms" }}>
            <div className="mb-2">
              <button
                className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border transition-all duration-300 ${
                  isTabActive("fundraise")
                    ? "border-teal-300 bg-teal-50 text-teal-700 font-semibold"
                    : "border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-gray-700"
                }`}
                onClick={() => {
                  setShowFundraiseDropdown(!showFundraiseDropdown);
                  setShowDonateDropdown(false);
                }}
              >
                <span>Fundraise</span>
                <i
                  className={`fas fa-chevron-down text-sm transition-transform ${
                    showFundraiseDropdown ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {showFundraiseDropdown && (
                <div className="mt-2 ml-4">
                  {/* Scrollable list in mobile - Show only 3 visible */}
                  <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                    {purposeOptions.map((option) => (
                      <button
                        key={option.value}
                        className="flex items-center w-full px-4 py-2.5 rounded-lg hover:bg-teal-50 transition-all duration-300 text-gray-700 text-left mb-1"
                        onClick={() => {
                          localStorage.setItem(
                            "preselectedPurpose",
                            option.value
                          );
                          setMenuOpen(false);
                          closeAllDropdowns();
                          window.location.href = "/start-fundraiser";
                        }}
                      >
                        <i className={`fas ${option.icon} text-teal-600 mr-3`}></i>
                        <span className="text-sm">{option.label}</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* General Fundraiser button */}
                  <button
                    onClick={() => {
                      localStorage.removeItem("preselectedPurpose");
                      setMenuOpen(false);
                      closeAllDropdowns();
                      window.location.href = "/start-fundraiser";
                    }}
                    className="flex items-center w-full px-4 py-3 rounded-lg bg-gradient-to-r from-teal-600 to-teal-700 text-white font-medium text-sm text-center justify-center mt-3"
                  >
                    Start General Fundraiser
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Profile Button */}
          {user && (
            <button
              onClick={() => {
                setMenuOpen(false);
                setProfileSheetOpen(true);
              }}
              className="w-full flex items-center justify-center px-4 py-3 rounded-lg bg-teal-50 text-teal-700 font-semibold border border-teal-200"
            >
              <i className="fas fa-user mr-2"></i>
              My Profile
            </button>
          )}

          {/* Sign in and Sign up buttons side by side for mobile */}
          {!user && (
            <div
              className="grid grid-cols-2 gap-3 animate-slideIn"
              style={{ animationDelay: "300ms" }}
            >
              <Link
                to="/signin"
                onClick={() => {
                  setMenuOpen(false);
                  closeAllDropdowns();
                }}
                className={`px-4 py-3 rounded-lg border transition-all duration-300 text-center ${
                  isTabActive("signin")
                    ? "border-teal-300 bg-teal-50 text-teal-700 font-semibold"
                    : "border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-gray-700"
                }`}
              >
                Sign in
              </Link>

              <Link
                to="/start-fundraiser"
                onClick={() => {
                  localStorage.removeItem("preselectedPurpose");
                  setMenuOpen(false);
                  closeAllDropdowns();
                }}
                className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-[1.02] text-center ${
                  isTabActive("fundraise")
                    ? "bg-gradient-to-r from-teal-600 to-teal-700 text-white"
                    : "bg-gradient-to-r from-teal-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700"
                }`}
              >
                Sign up
              </Link>
            </div>
          )}

          {/* Start Fundraiser Button - Mobile */}
          {!user && (
            <div
              className="animate-slideIn mt-3"
              style={{ animationDelay: "400ms" }}
            >
              <Link
                to="/start-fundraiser"
                onClick={() => {
                  localStorage.removeItem("preselectedPurpose");
                  setMenuOpen(false);
                  closeAllDropdowns();
                }}
                className={`w-full text-center block font-semibold px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl ${
                  isTabActive("fundraise")
                    ? "bg-gradient-to-r from-teal-700 to-teal-800 text-white"
                    : "bg-gradient-to-r from-[#00b7b3] to-[#009e9a] text-white"
                }`}
              >
                Start Your Fundraiser
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Profile Right Sheet */}
      {profileSheetOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-50"
            onClick={() => setProfileSheetOpen(false)}
          ></div>

          {/* Sheet */}
          <div className="fixed top-0 right-0 h-full w-[380px] max-w-[90%] bg-white z-50 shadow-2xl rounded-l-2xl overflow-hidden animate-slideInRight">
            {/* Header */}
            <div className="p-5 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {user?.name || "My Profile"}
                </h3>
                <p className="text-sm text-gray-500">{user?.email || ""}</p>
              </div>

              <button
                onClick={() => setProfileSheetOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 p-5">
              <div className="bg-gray-50 rounded-xl p-3 text-center shadow-sm">
                <h2 className="text-xl font-bold text-gray-800">0</h2>
                <p className="text-xs text-gray-500">Contributions done</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center shadow-sm">
                <h2 className="text-xl font-bold text-gray-800">0</h2>
                <p className="text-xs text-gray-500">Lives Impacted</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center shadow-sm">
                <h2 className="text-xl font-bold text-gray-800">0</h2>
                <p className="text-xs text-gray-500">Do good days</p>
              </div>
            </div>

            {/* Menu */}
            <div className="px-5 space-y-3">
              {[
                {
                  label: "My Contributions",
                  icon: "fa-hand-holding-heart",
                  path: "/my-contributions",
                },
                {
                  label: "My Fundraiser",
                  icon: "fa-heart",
                  path: "/my-fundraiser",
                },
                
              ].map((item) => (
                <button
                  key={item.label}
                  className="w-full flex items-center justify-between px-4 py-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                  onClick={() => {
                    setProfileSheetOpen(false);
                    navigate(item.path);
                  }}
                >
                  <div className="flex items-center gap-3 text-gray-700 font-medium">
                    <i className={`fas ${item.icon}`}></i>
                    <span>{item.label}</span>
                  </div>
                  <i className="fas fa-chevron-right text-gray-400"></i>
                </button>
              ))}

              {/* Logout */}
              <button
                className="w-full flex items-center justify-between px-4 py-4 bg-red-50 rounded-xl hover:bg-red-100 transition"
                onClick={handleLogout}
              >
                <div className="flex items-center gap-3 text-red-600 font-semibold">
                  <i className="fas fa-sign-out-alt"></i>
                  <span>Logout</span>
                </div>
                <i className="fas fa-chevron-right text-red-400"></i>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Animation styles */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes spinIn {
          from {
            transform: rotate(-90deg);
            opacity: 0;
          }
          to {
            transform: rotate(0);
            opacity: 1;
          }
        }

        @keyframes pulseSlow {
          0%,
          100% {
            transform: scale(1);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 10px 15px -3px rgba(13, 148, 136, 0.4);
          }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slideDown {
          animation: slideDown 0.5s ease-out;
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
          opacity: 0;
        }

        .animate-spinIn {
          animation: spinIn 0.3s ease-out;
        }

        .animate-pulse-slow {
          animation: pulseSlow 2s infinite;
        }

        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </nav>
  );
}